/**
 * Login Page - страница входа в систему
 *
 * Функционал:
 * - Email + Password форма
 * - Remember Me чекбокс (30 дней сессия)
 * - Валидация полей
 * - Показ ошибок
 * - Редирект на dashboard после успешного входа
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Checkbox,
  Alert,
  Text,
  Group,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useSession } from '../context/SessionContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Если уже авторизован - редирект на dashboard
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await login(email, password, rememberMe);

      if (result.success) {
        if (result.mustChangePassword) {
          console.log('User must change password on first login');
        } else {
          navigate('/', { replace: true });
        }
      } else {
        setError(result.error || 'Ошибка входа. Проверьте email и пароль.');
      }
    } catch (err) {
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size={420} my={40}>
      <Title ta="center" fw={900}>
        Добро пожаловать!
      </Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Войдите в свою учетную запись
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="lg">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            mb="md"
            radius="md"
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextInput
            label="Email"
            placeholder="your@email.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <PasswordInput
            label="Пароль"
            placeholder="Ваш пароль"
            required
            mt="md"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <Group justify="space-between" mt="lg">
            <Checkbox
              label="Запомнить меня"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.currentTarget.checked)}
              disabled={loading}
            />
          </Group>
          <Button
            type="submit"
            fullWidth
            mt={32}
            loading={loading}
          >
            Войти
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default LoginPage;
